import * as React from 'react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { message, Skeleton } from "antd";
import { MenuDto, BusinessDto } from "../../../gen/client";
import { getBusinessPageUrl, uuid } from "../../../util/Utils";
import { useHistory } from "react-router";
import MenuApi from "../../../api/MenuApi";
import { handleServerError } from "../../../util/ErrorHandler";
import MenuList from "./MenuList";
import DashboardMenu from "./DashboardMenu";
import styles from './DashboardMenus.module.scss';
import { getMenusPath, isDefaultPath, isMenusPath, RoutesEnum } from "../../../RoutesEnum";
import { MenuNode } from '../../../domain/Menu';
import { menuSubject } from '../../../store/Subjects';
import _ from 'lodash';
import { env } from '../../../env/env';
import RouteLeaveGuard from '../../misc/RouteLeaveGuard';
import MakeFirstMenuActiveModal from './MakeFirstMenuActiveModal';
import EmptyItemModal from './EmptyItemModal';
import MobilePreviewWrapper from "../MobilePreviewWrapper";
import { EmptyMenu } from '../../../util/constants';
import { setPageTitle } from "../../../util/seo";
import PublishButton from "../../PublishButton";
import { EyeOutlined, LinkOutlined } from '@ant-design/icons';
import { TimberMenuObject } from '../../../services/MenuService';

interface MenuProps {
  business: BusinessDto;
  menus: MenuDto[];
  menu: MenuDto;
  setMenu: (menu: MenuDto) => void;
  saving: boolean;
  publishing: boolean;
  isPublished: boolean;
  memberStatus: number;
  checkPublishingPossibility: () => void;
  onMenuAdded: (menu: MenuDto) => void;
  onMenuRemoved: (menuId: string) => void;
  onMenuEdited: (menu: MenuDto) => void;
  onCurrencyChange: (currency: string) => void;
  showShareModal: () => void;
  onMenuSort: (menus: MenuDto[]) => void;
  onSectionSort: (menu: MenuDto, sections: MenuNode[]) => void;
  onSave: (finished: boolean) => void;
  setCopyMenuModal: (visible: boolean) => void;
  onCopy: (isNavBySection: boolean, items: TimberMenuObject[]) => void;
}

export default function DashboardMenus({ business, menus, menu, setMenu, saving, publishing, isPublished, memberStatus, checkPublishingPossibility, onCurrencyChange, onMenuAdded, onMenuRemoved, onMenuEdited, onMenuSort, onSectionSort, onSave, setCopyMenuModal, onCopy }: MenuProps) {
  const history = useHistory();

  const [id, setId] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [emptyItemCount] = useState(0);
  const [isCreateSuccessModal, setCreateSuccessModal] = useState(false);
  const [isEmptyItemModal, setEmptyItemModal] = useState(false);
  const [scrollToBottom,] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null as MenuNode);
  const [manualSelectedSection, setManualSelectedSection] = useState(null as MenuNode);

  const saveChanges = useRef(_.debounce((newMenu: MenuDto, businessId: string) => {
    onSave(false);
    !newMenu.id ? create(newMenu, businessId) : update(newMenu);
  }, env.autoSaveDebounce));

  function update(menu: MenuDto) {
    MenuApi.updateMenu(menu)
      .then(() => onSave(true))
      .catch(handleServerError);
  }

  function create(menu: MenuDto, businessId: string) {
    MenuApi.saveMenu({
      ...menu,
      businessId,
      active: true
    }).then(resp => {
      onMenuAdded(resp.data);
      onSave(true);
      history.push(getMenusPath(business.id, resp.data.id));
    }).catch(handleServerError);
  }

  useEffect(() => {
    const unregister = history.listen((location) => {
      setCreateSuccessModal(!isMenusPath(location.pathname) && !!menus && !!menus.length && !menus.find(it => it.active));
    });

    return () => {
      unregister();
    }
  }, [menus, history]);

  useEffect(() => {
    const menuObs = menuSubject.subscribe((newMenu) => {
      setMenu(newMenu);
      onMenuEdited(newMenu);

      if (menu !== EmptyMenu) {
        saveChanges.current(newMenu, business.id);
      }
    });
    return () => {
      menuObs.unsubscribe();
    };
  }, [business.id, menu, onMenuEdited, setMenu]);

  function onMenuUpdate(updated: Partial<MenuDto>, scrollToBottom: boolean) {
    menuSubject.next({ ...menu, ...updated });
  }

  const fetchMenu = useCallback((path?: string) => {
    if (!path) path = getPathId(history.location.pathname);
    if (path === '/oo/app' || path === '/oostage/app') return;

    if (path === 'new') {
      setLoading(false);
      menuSubject.next({ nodes: JSON.stringify([new MenuNode(uuid())]) });
    } else {
      setLoading(true);
      MenuApi.getMenu(path).then((resp) => {
        const menu = resp.data;
        if (menu.removed) {
          message.error(`${'Menu not found'}!`, 3);
          return;
        }
        menuSubject.next(menu);
        setLoading(false);
      }).catch(err => {
        setLoading(false);
        handleServerError(err);
      });
    }
  }, [history.location.pathname]);

  useEffect(() => {
    setPageTitle('Menus');
    fetchMenu();
  }, [fetchMenu]);

  useEffect(() => {
    if (isDefaultPath(window.location.pathname)) {
      const active = menus.filter(it => !it.removed);
      const defaultMenuId = active.length > 0 ? active[0].id : 'new';
      history.push(getMenusPath(business.id, defaultMenuId));
    }
  }, [menus, business.id, history])

  useEffect(() => {
    return history.listen((location) => {
      if (!isMenusPath(location.pathname)) return;
      const path = getPathId(location.pathname);
      if (path === id) return;
      setId(path);
      fetchMenu(path);
    });
  }, [id, history.location, fetchMenu, business.id]); // eslint-disable-line

  function getPathId(pathname: string) {
    const bits = pathname.split(RoutesEnum.MenusPath + '/');
    return bits[bits.length - 1];
  }

  function onSectionClick(section: MenuNode, sectionMenu: MenuDto) {
    if (menu.id !== sectionMenu.id) {
      history.push(getMenusPath(business.id, sectionMenu.id));
    }
    setSelectedSection(section);
    setManualSelectedSection(section);
  }

  function onEmptyModalOk() {
    setEmptyItemModal(false);
  }

  return (<div>
    {/* {isPublished &&
      <div className={styles.siteLink}>
        <h6>Site link: <a href={getBusinessPageUrl(business)} target="_blank" rel="noreferrer">{getBusinessPageUrl(business)}</a></h6>
        <PublishButton business={business} saving={saving} publishing={publishing} isPublished={isPublished} memberStatus={memberStatus} checkPublishingPossibility={checkPublishingPossibility} />
      </div>
    } */}

    {isPublished &&
      <div className={styles.siteLink}>
        <div className={styles.views}>
          <h6><EyeOutlined /> {business.viewCount!} views</h6>
        </div>

        <div className={styles.actions}>
          <h6><LinkOutlined /> <a href={getBusinessPageUrl(business)} target="_blank" rel="noreferrer">{getBusinessPageUrl(business)}</a></h6>

          <PublishButton business={business} saving={saving} publishing={publishing} isPublished={isPublished} memberStatus={memberStatus} checkPublishingPossibility={checkPublishingPossibility} />
        </div>
      </div>
    }


    <div className={'step stacked'}>
      <div className={styles.splitter}>
        <MenuList business={business} menus={menus} active={menu} onMenuSort={onMenuSort} onSectionSort={onSectionSort} onSectionClick={onSectionClick} selectedSection={selectedSection} setCopyMenuModal={setCopyMenuModal} onCopy={onCopy} />

        {isLoading && <Skeleton className={styles.loading} active />}
        {!isLoading &&
          <DashboardMenu
            business={business}
            menu={menu}
            scrollToBottom={scrollToBottom}
            selectedSection={selectedSection}
            manualSelectedSection={manualSelectedSection}
            setManualSelectedSection={setManualSelectedSection}
            published={isPublished}
            setSelectedSection={setSelectedSection}
            onCurrencyChange={onCurrencyChange}
            onMenuRemoved={onMenuRemoved}
            onMenuUpdate={onMenuUpdate}
            menus={menus}
          />}
      </div>

      <MobilePreviewWrapper menus={menus} business={business} isPublished={isPublished} />
    </div>

    <RouteLeaveGuard enabled={isCreateSuccessModal} element={<MakeFirstMenuActiveModal isVisible={true} onCancel={() => setCreateSuccessModal(false)} onOk={() => onMenuUpdate({ active: true }, false)} />} />

    <EmptyItemModal isVisible={isEmptyItemModal} count={emptyItemCount} onOk={onEmptyModalOk} onCancel={() => setEmptyItemModal(false)} />
  </div>);
};
